import { useState, useMemo } from 'react'
import { Box, Button, Flex, Text, Grid } from '@chakra-ui/react'
import { Plus } from 'lucide-react'
import { BankAccountCard } from './BankAccountCard'
import { BankAccountEmptyCard } from './BankAccountEmptyCard'
import { BankAccountFormModal } from './BankAccountFormModal'
import { useBankAccounts, useDeleteBankAccount } from '../hooks/useBankAccounts'
import type { BankAccount, BankAccountType } from '../types'
import { toaster } from '@/shared/lib/toaster'
import {
  DialogRoot,
  DialogBackdrop,
  DialogPositioner,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogCloseTrigger,
} from '@chakra-ui/react'

interface BankAccountsListProps {
  residenceId: string
}

export function BankAccountsList({ residenceId }: BankAccountsListProps) {
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState<BankAccount | undefined>()
  const [accountToDelete, setAccountToDelete] = useState<BankAccount | null>(null)
  const [prefilledType, setPrefilledType] = useState<BankAccountType | undefined>()
  const [prefilledIsPrimary, setPrefilledIsPrimary] = useState<boolean | undefined>()

  const { data: accounts = [], isLoading } = useBankAccounts({
    residence_id: residenceId,
    is_active: true,
  })

  const deleteBankAccount = useDeleteBankAccount()

  // Grouper les comptes par type
  const { currentAccount, alurAccount, savingsAccount, otherAccounts } = useMemo(() => {
    const current = accounts.find((acc) => acc.account_type === 'current')
    const alur = accounts.find((acc) => acc.account_type === 'alur_reserve')
    const savings = accounts.find((acc) => acc.account_type === 'savings')
    const others = accounts.filter(
      (acc) =>
        acc.account_type !== 'current' &&
        acc.account_type !== 'alur_reserve' &&
        (acc.account_type !== 'savings' || savings) // Garder les comptes épargne supplémentaires
    )

    return {
      currentAccount: current,
      alurAccount: alur,
      savingsAccount: savings,
      otherAccounts: others,
    }
  }, [accounts])

  const handleEdit = (account: BankAccount) => {
    setSelectedAccount(account)
    setPrefilledType(undefined)
    setPrefilledIsPrimary(undefined)
    setIsFormOpen(true)
  }

  const handleDelete = (account: BankAccount) => {
    setAccountToDelete(account)
  }

  const handleCreateWithType = (type: BankAccountType, isPrimary = false) => {
    setSelectedAccount(undefined)
    setPrefilledType(type)
    setPrefilledIsPrimary(isPrimary)
    setIsFormOpen(true)
  }

  const handleCreateOther = () => {
    setSelectedAccount(undefined)
    setPrefilledType(undefined)
    setPrefilledIsPrimary(false)
    setIsFormOpen(true)
  }

  const confirmDelete = async () => {
    if (!accountToDelete) return

    try {
      await deleteBankAccount.mutateAsync(accountToDelete.id)
      toaster.create({
        title: 'Succès',
        description: 'Compte bancaire supprimé avec succès',
        type: 'success',
      })
      setAccountToDelete(null)
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Erreur lors de la suppression'
      toaster.create({ title: 'Erreur', description: message, type: 'error' })
    }
  }

  const handleCloseForm = () => {
    setIsFormOpen(false)
    setSelectedAccount(undefined)
    setPrefilledType(undefined)
    setPrefilledIsPrimary(undefined)
  }

  if (isLoading) {
    return (
      <Box p={8} textAlign="center">
        <Text color="fg.muted">Chargement des comptes bancaires...</Text>
      </Box>
    )
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Text fontSize="lg" fontWeight="semibold">
            Comptes bancaires
          </Text>
          <Text fontSize="sm" color="fg.muted">
            {accounts.length} compte{accounts.length > 1 ? 's' : ''}
          </Text>
        </Box>
        <Button
          variant="outline"
          colorPalette="gray"
          size="sm"
          onClick={handleCreateOther}
        >
          <Plus />
          Ajouter un autre compte
        </Button>
      </Flex>

      {/* Comptes principaux (3 cards fixes) */}
      <Box mb={6}>
        <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
          {/* Compte courant */}
          {currentAccount ? (
            <BankAccountCard
              account={currentAccount}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ) : (
            <BankAccountEmptyCard
              accountType="current"
              onCreate={() => handleCreateWithType('current', true)}
            />
          )}

          {/* Compte ALUR */}
          {alurAccount ? (
            <BankAccountCard
              account={alurAccount}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ) : (
            <BankAccountEmptyCard
              accountType="alur_reserve"
              onCreate={() => handleCreateWithType('alur_reserve')}
            />
          )}

          {/* Compte épargne */}
          {savingsAccount ? (
            <BankAccountCard
              account={savingsAccount}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ) : (
            <BankAccountEmptyCard
              accountType="savings"
              onCreate={() => handleCreateWithType('savings')}
            />
          )}
        </Grid>
      </Box>

      {/* Comptes additionnels */}
      {otherAccounts.length > 0 && (
        <Box>
          <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={3}>
            Comptes additionnels
          </Text>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={4}>
            {otherAccounts.map((account) => (
              <BankAccountCard
                key={account.id}
                account={account}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </Grid>
        </Box>
      )}

      {/* Form Modal */}
      <BankAccountFormModal
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        residenceId={residenceId}
        account={selectedAccount}
        prefilledType={prefilledType}
        prefilledIsPrimary={prefilledIsPrimary}
      />

      {/* Delete Confirmation Dialog */}
      <DialogRoot
        open={!!accountToDelete}
        onOpenChange={(e) => !e.open && setAccountToDelete(null)}
        size="sm"
      >
        <DialogBackdrop />
        <DialogPositioner>
          <DialogContent>
            <DialogCloseTrigger />
            <DialogHeader>
              <DialogTitle>Supprimer le compte bancaire ?</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <Text>
                Êtes-vous sûr de vouloir supprimer le compte{' '}
                <strong>{accountToDelete?.label}</strong> ?
              </Text>
              <Text mt={2} fontSize="sm" color="red.600">
                Cette action est irréversible. Assurez-vous qu'aucune opération n'est liée à ce compte.
              </Text>
            </DialogBody>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setAccountToDelete(null)}>
                Annuler
              </Button>
              <Button
                colorPalette="red"
                onClick={confirmDelete}
                loading={deleteBankAccount.isPending}
              >
                Supprimer
              </Button>
            </DialogFooter>
          </DialogContent>
        </DialogPositioner>
      </DialogRoot>
    </Box>
  )
}
